// src/authpages/timetables/GoogleCalendarUtils.js

/**
 * Initialize the Google API client
 * @param {string} apiKey - The Google API key
 * @returns {Promise<boolean>} - Whether initialization was successful
 */
export const initGoogleApiClient = async (apiKey) => {
  if (!window.gapi) {
    console.error('Google API client not loaded');
    return false;
  }
  
  if (!window.gapi.client) {
    await new Promise((resolve) => {
      window.gapi.load('client', resolve);
    });
  }
  
  try {
    await window.gapi.client.init({
      apiKey: apiKey,
      discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'],
    });
    return true;
  } catch (error) {
    console.error('Error initializing Google API client:', error);
    return false;
  }
};

/**
 * Fetch events from Google Calendar
 * @param {string} accessToken - The Google OAuth access token
 * @param {number} daysAhead - Number of days ahead to fetch events for
 * @returns {Promise<Array>} - Formatted calendar events
 */
export const fetchGoogleCalendarEvents = async (accessToken, daysAhead = 30) => {
  try {
    // Set the access token
    window.gapi.client.setToken({
      access_token: accessToken
    });
    
    // Get current time
    const now = new Date();
    
    // Set time range
    const timeMin = now.toISOString();
    const futureDate = new Date(now);
    futureDate.setDate(now.getDate() + daysAhead);
    const timeMax = futureDate.toISOString();
    
    // Make API call to get events
    const response = await window.gapi.client.calendar.events.list({
      'calendarId': 'primary',
      'timeMin': timeMin,
      'timeMax': timeMax,
      'showDeleted': false,
      'singleEvents': true,
      'maxResults': 100,
      'orderBy': 'startTime'
    });
    
    // Process the response
    const googleEvents = response.result.items;
    
    // Format events for the calendar view
    const formattedEvents = googleEvents.map(event => {
      const start = event.start.dateTime ? new Date(event.start.dateTime) : new Date(event.start.date);
      const end = event.end.dateTime ? new Date(event.end.dateTime) : new Date(event.end.date);
      
      // If it's an all-day event (no dateTime), set appropriate times
      if (!event.start.dateTime) {
        start.setHours(0, 0, 0);
      }
      
      if (!event.end.dateTime) {
        end.setHours(23, 59, 59);
      }
      
      return {
        id: `google-${event.id}`,
        title: event.summary || 'Untitled Event',
        start: start,
        end: end,
        provider: 'Google Calendar',
        color: '#4285F4', // Google blue
        description: event.description || '',
        location: event.location || '',
        allDay: !event.start.dateTime
      };
    });
    
    return formattedEvents;
  } catch (error) {
    console.error('Error fetching Google Calendar events:', error);
    throw new Error('Failed to fetch events from Google Calendar');
  }
};